<template>
  <v-main>
    <v-alert id="alert" v-model="alert.isAlert" :type="alert.type" closable class="row">{{ alert.message }}</v-alert>
    <v-overlay :model-value="showOverlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-container fluid>
      <h1>連絡先</h1>
      <div>※空電における、緊急もしくはお知らせのEmailをご登録ください。</div>
    </v-container>
    <v-container v-for="(billing, key) in billingList" :key="key" fluid>
      <v-card name="accounts">
        <v-card-title>
          <v-row>
            <v-col cols="12" sm="6" md="1">
              <v-text-field name="billingId" :model-value="billing.billing_id" label="契約ID" readonly density="compact" variant="underlined"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                name="billingName"
                :model-value="billing.billing_name"
                label="法人名"
                readonly
                density="compact"
                variant="underlined"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                name="billingSectionName"
                :model-value="billing.billing_section_name"
                label="部署"
                readonly
                density="compact"
                variant="underlined"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                name="billingRepresentativeName"
                :model-value="billing.billing_representative_name"
                label="担当者"
                readonly
                density="compact"
                variant="underlined"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="6" class="mt-n8">
              <v-text-field
                v-model="billing.search"
                name="search"
                :append-inner-icon="mdiMagnify"
                label="絞り込み検索"
                variant="underlined"
                double-line
                hide-details
                @update:model-value="onSearch($event, billing.billing_id)"
              ></v-text-field>
            </v-col>
            <v-col v-if="isEditable()" cols="12" sm="2" md="2" offset-md="4" class="mt-n6 add-btn-height">
              <!-- 新規登録ボタン -->
              <v-toolbar flat color="transparent">
                <v-spacer></v-spacer>
                <v-btn color="primary" variant="elevated" class="mt-n4" @click="edit('', key)">
                  <v-icon :icon="mdiPlus"></v-icon>
                  新規登録
                </v-btn>
              </v-toolbar>
            </v-col>
          </v-row>
        </v-card-title>

        <!-- 緊急連絡先一覧 -->
        <v-data-table
          v-model:page="billing.page"
          :headers="headers"
          :items="billing.displayContactList"
          :items-per-page="billing.itemsPerPage"
          :search="billing.search"
          hide-default-footer
          class="elevation-1"
          density="compact"
        >
          <template #[`item.contact_email`]="props">
            <div class="restrict-email">{{ props.item.contact_email }}</div>
          </template>

          <template #[`item.company_name`]="props">
            <div class="restrict-email">{{ props.item.company_name }}</div>
          </template>

          <template #[`item.name`]="props">
            <div class="restrict-email">{{ props.item.name }}</div>
          </template>

          <template #[`item.emergency`]="props">
            <v-checkbox-btn v-model="props.item.emergency" disabled></v-checkbox-btn>
          </template>

          <template #[`item.information`]="props">
            <v-checkbox-btn v-model="props.item.information" disabled></v-checkbox-btn>
          </template>

          <template v-if="isEditable()" #[`item.actions`]="props">
            <div class="td-button-inline">
              <v-btn size="small" color="primary" class="td-icon-left" @click="edit(props.item, key)"> 編集 </v-btn>
              <v-btn size="small" color="error" class="td-icon-right" @click="removeConfirm(props.item, key)"> 削除 </v-btn>
            </div>
          </template>

          <template #bottom="props">
            <div class="text-center pt-2">
              <v-pagination
                v-model="billing.page"
                :length="props.pageCount"
                @update:model-value="onPageChange($event, billing.billing_id)"
              ></v-pagination>
              <v-text-field
                :model-value="props.itemsPerPage"
                class="pa-2"
                :label="perPageText"
                type="number"
                hide-details
                @update:model-value="onUpdateItemsPerPage($event, billing)"
              ></v-text-field>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- 新規登録・編集ダイアログ -->
    <template>
      <v-form ref="form" lazy-validation>
        <v-dialog v-model="dialog" persistent max-width="640px">
          <v-card>
            <v-card-title>
              <div class="text-h5">連絡先 <span v-if="editedItem.isNew">新規登録</span><span v-else>変更</span></div>
            </v-card-title>

            <v-card-text>
              <v-container>
                <v-row class="dialog-custom-row">
                  <v-col cols="4">
                    <v-text-field v-model="editedItem.billing_id" label="契約ID" readonly disabled></v-text-field>
                  </v-col>
                  <v-col cols="8">
                    <v-text-field v-model="editedItem.billing_name" label="法人名" readonly disabled></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-select v-if="editedItem.isNew" v-model="editedItem.billing_profile_id" :items="selectedProfiles" label="プロファイルID"></v-select>
                    <v-text-field v-else :model-value="editedItem.profile_id" label="プロファイルID" readonly disabled></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="editedItem.mailaddress_type"
                      :rules="[rules.required]"
                      :items="mailaddressTypeOptions"
                      label="メールアドレス種別">
                    </v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editedItem.new_contact_email"
                      :rules="[rules.required, rules.email, validateContactMail()]"
                      maxlength="256"
                      label="メールアドレス"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editedItem.company_name"
                      :rules="[companyNameRules]"
                      maxlength="64"
                      label="会社名"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editedItem.name"
                      :rules="[nameRules]"
                      maxlength="64"
                      label="氏名"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox v-model="selectedContactType" color="primary" :error="changeCheckboxColor" :rules="[ruleContactPurpose]" value="emergency" label="緊急"></v-checkbox>
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox
                      v-model="selectedContactType"
                      color="primary"
                      :error="changeCheckboxColor"
                      :rules="[ruleContactPurpose]"
                      value="information"
                      label="お知らせ"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-card-text align="left">
                <v-btn variant="elevated" @click="close"> キャンセル </v-btn>
              </v-card-text>
              <v-card-text align="right">
                <v-btn color="primary" variant="elevated" absolute :disabled="isButtonDisabled" @click="save">
                  <span v-if="editedItem.isNew">登録</span>
                  <span v-else>更新</span>
                </v-btn>
              </v-card-text>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-form>
    </template>

    <!-- アカウント削除ダイアログ -->
    <template>
      <v-dialog v-model="dialogDelete" persistent max-width="500px">
        <v-card>
          <v-card-title class="text-h5">削除しますか?</v-card-title>
          <v-card-actions>
            <v-card-text align="left">
              <v-btn variant="elevated" @click="closeRemove">キャンセル</v-btn>
            </v-card-text>
            <v-card-text align="right">
              <v-btn color="error" variant="elevated" :disabled="isButtonDisabled" @click="remove">削除する</v-btn>
            </v-card-text>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-main>
</template>

<script>
import Api from '@/constants/api';
import UserRole from '@/constants/userrole';
import Pagination from '@/constants/pagination';
import { mdiMagnify, mdiPlus } from '@mdi/js';
const AltProfileId = 'すべてのプロファイル';

export default {
  data: () => ({
    dialog: false,
    dialogDelete: false,
    editedBillingIndex: -1,
    editedItem: {
      billing_id: '',
      billing_name: '',
      billing_section_name: '',
      billing_representative_name: '',
      billing_profile_id: '',
      profile_id: '',
      profile_name: '',
      emergency: false,
      information: false,
      contact_email: '',
      new_contact_email: '',
      isNew: false,
      mailaddress_type: '',
      company_name: '',
      name: '',
    },
    defaultItem: {
      billing_id: '',
      billing_name: '',
      billing_section_name: '',
      billing_representative_name: '',
      billing_profile_id: '',
      profile_id: '',
      profile_name: '',
      emergency: false,
      information: false,
      contact_email: '',
      new_contact_email: '',
      isNew: false,
      mailaddress_type: '',
      company_name: '',
      name: '',
    },
    mailaddressTypeOptions: [
      { title: "", value: ""},
      { title: "契約責任者", value: 1},
      { title: "契約担当者", value: 2},
      { title: "SMS実務担当者", value: 3},
      { title: "経理担当者", value: 4},
      { title: "システム担当者", value: 5},
      { title: "メーリングリスト", value: 6},
      { title: "その他", value: 7},
    ],
    headers: [
      { title: 'プロファイルID', key: 'profile_id', width: '18%' },
      { title: 'プロファイル名称', key: 'profile_name', width: '19%' },
      { title: '利用開始日', key: 'profile_start_date', filterable: false, width: '11%' },
      { title: '利用停止日', key: 'profile_end_date', filterable: false, width: '11%' },
      { title: 'メールアドレス', key: 'contact_email', width: '10%' },
      { title: '緊急', key: 'emergency', filterable: false, width: '10%' },
      { title: 'お知らせ', key: 'information', filterable: false, width: '13%' },
      { title: '', key: 'actions', filterable: false, sortable: false, width: '8%' },
    ],
    billingList: [],
    selectedProfiles: [],
    selectedContactType: [],
    allContactType: ['emergency', 'information'],
    sortBy: '',
    sortDesc: true,
    showOverlay: false,
    isButtonDisabled: false,
    loginUser: {
      billinggroupId: '',
      roleId: -1,
    },
    rules: {
      required: (value) => !!value || '必須項目です。',
      email: (value) => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value) || 'メールアドレスが間違っています。',
    },
    alert: { isAlert: false, type: 'success', message: '' },
    mdiMagnify,
    mdiPlus,
    paginationState: [
      {
        billingId: '',
        items: {
          currentPage: 1,
          perPage: 10,
          searchWord: '',
        },
      },
    ]
  }),
  computed: {
    ruleContactPurpose() {
      return this.selectedContactType.length > 0 || '1つ以上選択してください。';
    },
    changeCheckboxColor() {
      return this.selectedContactType.length <= 0;
    },
    perPageText() {
      return Pagination.ITEMS_PER_PAGE_TEXT_WITH_NOTICE;
    },
    companyNameRules() {
      console.log(this.editedItem.company_name)
      if (this.editedItem.mailaddress_type === 6 || (this.editedItem.company_name !== "" && this.editedItem.company_name !== undefined)) return true;
      else return false || '必須項目です。';
    },
    nameRules() {
      if (this.editedItem.mailaddress_type === 6 || (this.editedItem.name !== "" && this.editedItem.name !== undefined)) return true;
      else return false || '必須項目です。';
    },
  },
  created: async function () {
    this.showOverlay = true;
    try {
      await this.initialize();
    } finally {
      this.showOverlay = false;
    }
  },
  methods: {
    initialize: async function () {
      const accessToken = this.$cookies.get('access_token');
      console.log('accessToken=' + accessToken);

      let sessionResponse = await this.axios.get(Api.PATH_SESSION);
      console.log(sessionResponse);
      if (sessionResponse.data.status == Api.STATUS_SUCCESS) {
        this.loginUser.billinggroupId = sessionResponse.data.session.billinggroup_id;
        this.loginUser.roleId = sessionResponse.data.session.role_id;
      } else {
        this.$cookies.remove('access_token');
        this.$router.push({ name: 'Login' });
        return;
      }
      if (!this.loginUser.billinggroupId) {
        console.log('billinggroupId is invalid.');
        return;
      }

      let billingResponse = await this.axios.get(Api.PATH_BILLING, {
        params: {
          billinggroup_id: this.loginUser.billinggroupId,
        },
      });
      console.log(billingResponse);

      if (billingResponse.data.status == Api.STATUS_SUCCESS) {
        this.billingList = billingResponse.data.billing_list;
        this.createDisplayContactList();
        this.createPaginationState();
        this.billingList
          .forEach((billing) => {
            this.sortBillingDisplayContactList(billing);
          });
        console.log(this.billingList);
      } else {
        throw 'API_BILLINGS failed. status=' + billingResponse.data.status;
      }
      if (!this.billingList) {
        console.log('billingList is invalid.');
        return;
      }
    },
    createDisplayContactList: function () {
      this.billingList.forEach((billing) => {
        billing.displayContactList = [];
        if (billing.contact_list) {
          billing.contact_list.forEach((contact) => billing.displayContactList.push(this.createDisplayContact(billing, null, contact)));
        } else {
          billing.contact_list = [];
        }
        if (billing.profile_list) {
          billing.profile_list.forEach((profile) => {
            if (profile.contact_list) {
              profile.contact_list.forEach((contact) => billing.displayContactList.push(this.createDisplayContact(billing, profile, contact)));
            } else {
              profile.contact_list = [];
            }
          });
        } else {
          billing.profile_list = [];
        }
      });
    },
    createPaginationState: function () {
      this.billingList.forEach((billing, i) => {
        const state = this.paginationState.find((state) => state.billingId == billing.billing_id);
        if (state === undefined) {
          this.paginationState[i] = {'billingId': billing.billing_id, items: {currentPage: 1, perPage: 10, searchWord: ''}};
        } else {
          billing.page = state.items.currentPage;
          billing.itemsPerPage = state.items.perPage;
          billing.search = state.items.searchWord;
        }
      });
    },
    initBillingDisplayContactList: function (billing) {
      const list = [];
      billing.contact_list.forEach((contact) => list.push(this.createDisplayContact(billing, null, contact)));
      billing.profile_list.forEach((profile) => profile.contact_list.forEach((contact) => list.push(this.createDisplayContact(billing, profile, contact))));
      return list;
    },
    sortBillingDisplayContactList: function (billing) {
      // すべてのプロファイルの連絡先：メールアドレス昇順
      // すべてのプロファイルの連絡先：メールアドレスが空（空文字はBFF側で除去されるためundefinedのみ）
      // プロファイルごとの連絡先：プロファイル開始日時の降順,プロファイルID昇順,メールアドレス昇順
      // プロファイルごとの連絡先：プロファイル開始日時が空の場合はプロファイルID昇順,メールアドレス昇順
      const sortableCommon = billing.displayContactList
        .filter((contact) => contact.billing_profile_id === AltProfileId)
        .filter((contact) => contact.contact_email != null)
        .sort((a, b) => a.contact_email.toLowerCase().localeCompare(b.contact_email.toLowerCase()));
      const unsortableCommon = billing.displayContactList
        .filter((contact) => contact.billing_profile_id === AltProfileId)
        .filter((contact) => contact.contact_email == null);
      const sortableProfile = billing.displayContactList
        .filter((contact) => contact.billing_profile_id !== AltProfileId)
        .filter((contact) => contact.profile_start_date != null)
        .sort((a, b) => {
          let dataCompare = b.profile_start_date.localeCompare(a.profile_start_date);
          if (dataCompare != 0) {
            return dataCompare;
          }
          let profileIdCompare = a.billing_profile_id.localeCompare(b.billing_profile_id);
          if (profileIdCompare != 0) {
            return profileIdCompare;
          }
          return a.contact_email.toLowerCase().localeCompare(b.contact_email.toLowerCase());
        });
      const unsortableProfile = billing.displayContactList
        .filter((contact) => contact.billing_profile_id !== AltProfileId)
        .filter((contact) => contact.profile_start_date == null)
        .sort((a, b) => {
          let profileIdCompare = a.billing_profile_id.localeCompare(b.billing_profile_id);
          if (profileIdCompare != 0) {
            return profileIdCompare;
          }
          return a.contact_email.toLowerCase().localeCompare(b.contact_email.toLowerCase());
        });
      billing.displayContactList = sortableCommon.concat(unsortableCommon).concat(sortableProfile).concat(unsortableProfile);
    },
    createDisplayContact: function (billing, profile, contact) {
      console.log(contact)
      return {
        billing_id: billing.billing_id,
        billing_name: billing.billing_name,
        billing_section_name: billing.billing_section_name,
        billing_representative_name: billing.billing_representative_name,
        billing_profile_id: profile == null ? AltProfileId : profile.billing_profile_id,
        profile_start_date: profile == null ? '' : profile.profile_start_date,
        profile_end_date: profile == null ? '' : profile.profile_end_date,
        profile_id: profile == null ? AltProfileId : profile.profile_id,
        profile_name: profile == null ? '' : profile.profile_name,
        contact_email: contact.contact_email,
        emergency: contact.is_emergency == 1,
        information: contact.is_information == 1,
        mailaddress_type: contact.mailaddress_type ?? '',
        company_name: contact.company_name,
        name: contact.name,
      };
    },
    edit: function (item, key) {
      this.$refs.form.resetValidation();
      this.editedBillingIndex = key;
      const contact = this.billingList[key];
      this.editedItem = Object.assign({}, item);
      this.editedItem.billing_id = contact.billing_id;
      this.editedItem.billing_name = contact.billing_name;
      if (item == '') {
        this.editedItem.new_contact_email = '';
        this.editedItem.isNew = true;
        this.editedItem.billing_profile_id = AltProfileId;
        this.editedItem.mailaddress_type = '';
        this.editedItem.company_name = '';
        this.editedItem.name = '';
        this.selectedContactType = this.allContactType.slice();
        // プロファイルselectボックス生成
        this.selectedProfiles = [{ title: AltProfileId, value: AltProfileId }];
        contact.profile_list.forEach((profile) => this.selectedProfiles.push({ title: profile.profile_id, value: profile.billing_profile_id }));
      } else {
        this.editedItem.new_contact_email = this.editedItem.contact_email;
        this.selectedContactType = this.allContactType.filter((type) => this.editedItem[type]);
      }
      this.dialog = true;
      console.log('editContact', this.editedItem);
    },
    validateContactMail: function () {
      let valmsg = '';
      if (this.editedItem.contact_email !== this.editedItem.new_contact_email) {
        const duplicate = this.billingList[this.editedBillingIndex].displayContactList
          .filter((row) => row.billing_profile_id === this.editedItem.billing_profile_id)
          .find((contact) => contact.contact_email === this.editedItem.new_contact_email);
        if (duplicate) {
          valmsg = '既に登録済みのメールアドレスです。';
          return valmsg;
        }
      }
      return valmsg === '' ? true : valmsg;
    },
    save: async function () {
      this.isButtonDisabled = true;
      if (!this.isEditable()) {
        throw 'API_ACCOUNT_CREATE_USER unauthorized.';
      }
      const validateResult = await this.$refs.form.validate();
      if (!validateResult.valid || this.validateContactMail() !== true) {
        console.log('validate error.');
        this.isButtonDisabled = false;
        return;
      }
      let alertType;
      let message;
      if (this.editedItem.isNew) {
        // 登録
        console.log('add new contact', this.editedItem);
        const response = await this.createContact();
        if (response.data.status == Api.STATUS_SUCCESS) {
          alertType = 'success';
          message = this.editedItem.billing_id + 'の連絡先情報を登録しました。';
          await this.initialize();
          this.billingList[this.editedBillingIndex].displayContactList = this.initBillingDisplayContactList(this.billingList[this.editedBillingIndex]);
          this.sortBillingDisplayContactList(this.billingList[this.editedBillingIndex]);
        } else {
          alertType = 'error';
          message = '登録に失敗しました。';
        }
      } else {
        // 更新
        console.log('update contact', this.editedItem);
        const response = await this.updateContact();
        if (response.data.status == Api.STATUS_SUCCESS) {
          alertType = 'success';
          message = this.editedItem.billing_id + 'の連絡先情報を更新しました。';
          await this.initialize();
          this.billingList[this.editedBillingIndex].displayContactList = this.initBillingDisplayContactList(this.billingList[this.editedBillingIndex]);
          this.sortBillingDisplayContactList(this.billingList[this.editedBillingIndex]);
        } else if (response.data.status == Api.STATUS_CONTACT_CONSTRAINT_VIOLATION) {
          alertType = 'warning';
          message = '各プロファイルに対し、最低でも1件、緊急時とお知らせ時に使用できる連絡先を残してください。';
        } else {
          alertType = 'error';
          message = '更新に失敗しました。';
        }
      }
      this.isButtonDisabled = false;
      this.close();
      this.showAlert(alertType, message);
      setTimeout(() => window.scrollTo({ top: 0 }), 0);
    },
    removeConfirm: function (item, key) {
      this.editedBillingIndex = key;
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
      console.log('removeConfirm', this.editedItem);
    },
    remove: async function () {
      this.isButtonDisabled = true;
      console.log('remove');
      const response = await this.removeContact();
      if (response.data.status == Api.STATUS_SUCCESS) {
        this.billingList[this.editedBillingIndex].displayContactList = this.initBillingDisplayContactList(this.billingList[this.editedBillingIndex]);
        this.sortBillingDisplayContactList(this.billingList[this.editedBillingIndex]);
        this.showAlert('success', this.billingList[this.editedBillingIndex].billing_id + 'の連絡先情報を削除しました。');
      } else if (response.data.status == Api.STATUS_CONTACT_CONSTRAINT_VIOLATION) {
        this.showAlert('warning', '各プロファイルに対し、最低でも1件、緊急時とお知らせ時に使用できる連絡先を残してください。');
      } else {
        this.showAlert('error', '削除に失敗しました。');
      }
      this.isButtonDisabled = false;
      this.closeRemove();
      setTimeout(() => window.scrollTo({ top: 0 }), 0);
    },
    createContact: async function () {
      if (this.editedItem.billing_profile_id === AltProfileId) {
        const backup = this.billingList[this.editedBillingIndex].contact_list.map((contact) => ({ ...contact }));
        this.billingList[this.editedBillingIndex].contact_list.push({
          contact_email: this.editedItem.new_contact_email,
          is_emergency: this.selectedContactType.includes('emergency') ? 1 : 0,
          is_information: this.selectedContactType.includes('information') ? 1 : 0,
          mailaddress_type: this.editedItem.mailaddress_type,
          company_name: this.editedItem.company_name,
          name: this.editedItem.name,
        });
        const response = await this.postBillingContactList(this.editedItem.billing_id, this.billingList[this.editedBillingIndex].contact_list);
        if (response.data.status !== Api.STATUS_SUCCESS) {
          this.billingList[this.editedBillingIndex].contact_list = backup;
        }
        return response;
      } else {
        const profile = this.billingList[this.editedBillingIndex].profile_list.find(
          (profile) => profile.billing_profile_id === this.editedItem.billing_profile_id
        );
        const backup = profile.contact_list.map((contact) => ({ ...contact }));
        profile.contact_list.push({
          contact_email: this.editedItem.new_contact_email,
          is_emergency: this.selectedContactType.includes('emergency') ? 1 : 0,
          is_information: this.selectedContactType.includes('information') ? 1 : 0,
          mailaddress_type: this.editedItem.mailaddress_type,
          company_name: this.editedItem.company_name,
          name: this.editedItem.name,
        });
        const response = await this.postProfileContactList(this.editedItem.billing_profile_id, profile.contact_list);
        if (response.data.status !== Api.STATUS_SUCCESS) {
          profile.contact_list = backup;
        }
        return response;
      }
    },
    updateContact: async function () {
      if (this.editedItem.billing_profile_id === AltProfileId) {
        const backup = this.billingList[this.editedBillingIndex].contact_list.map((contact) => ({ ...contact }));
        const target = this.billingList[this.editedBillingIndex].contact_list.find((contact) => contact.contact_email === this.editedItem.contact_email);
        target.contact_email = this.editedItem.new_contact_email;
        target.is_emergency = this.selectedContactType.includes('emergency') ? 1 : 0;
        target.is_information = this.selectedContactType.includes('information') ? 1 : 0;
        target.mailaddress_type = this.editedItem.mailaddress_type;
        target.company_name = this.editedItem.company_name;
        target.name = this.editedItem.name;
        const response = await this.postBillingContactList(this.editedItem.billing_id, this.billingList[this.editedBillingIndex].contact_list);
        if (response.data.status !== Api.STATUS_SUCCESS) {
          this.billingList[this.editedBillingIndex].contact_list = backup;
        }
        return response;
      } else {
        const profile = this.billingList[this.editedBillingIndex].profile_list.find(
          (profile) => profile.billing_profile_id === this.editedItem.billing_profile_id
        );
        const backup = profile.contact_list.map((contact) => ({ ...contact }));
        const target = profile.contact_list.find((contact) => contact.contact_email === this.editedItem.contact_email);
        target.contact_email = this.editedItem.new_contact_email;
        target.is_emergency = this.selectedContactType.includes('emergency') ? 1 : 0;
        target.is_information = this.selectedContactType.includes('information') ? 1 : 0;
        target.mailaddress_type = this.editedItem.mailaddress_type;
        target.company_name = this.editedItem.company_name;
        target.name = this.editedItem.name;
        const response = await this.postProfileContactList(this.editedItem.billing_profile_id, profile.contact_list);
        if (response.data.status !== Api.STATUS_SUCCESS) {
          profile.contact_list = backup;
        }
        return response;
      }
    },
    removeContact: async function () {
      if (this.editedItem.billing_profile_id === AltProfileId) {
        const backup = this.billingList[this.editedBillingIndex].contact_list.map((contact) => ({ ...contact }));
        this.billingList[this.editedBillingIndex].contact_list = this.billingList[this.editedBillingIndex].contact_list.filter(
          (contact) => contact.contact_email !== this.editedItem.contact_email
        );
        const response = await this.postBillingContactList(this.editedItem.billing_id, this.billingList[this.editedBillingIndex].contact_list);
        if (response.data.status !== Api.STATUS_SUCCESS) {
          this.billingList[this.editedBillingIndex].contact_list = backup;
        }
        return response;
      } else {
        const profile = this.billingList[this.editedBillingIndex].profile_list.find(
          (profile) => profile.billing_profile_id === this.editedItem.billing_profile_id
        );
        const backup = profile.contact_list.map((contact) => ({ ...contact }));
        profile.contact_list = profile.contact_list.filter((contact) => contact.contact_email !== this.editedItem.contact_email);
        const response = await this.postProfileContactList(this.editedItem.billing_profile_id, profile.contact_list);
        if (response.data.status !== Api.STATUS_SUCCESS) {
          profile.contact_list = backup;
        }
        return response;
      }
    },
    postBillingContactList: async function (billingId, contactList) {
      const accessToken = this.$cookies.get('access_token');
      const billingResponse = await this.axios.post(Api.PATH_BILLING_UPDATE, {
        access_token: accessToken,
        billing_id: billingId,
        contact_list: contactList,
      });
      return billingResponse;
    },
    postProfileContactList: async function (billingProfileId, contactList) {
      const accessToken = this.$cookies.get('access_token');
      let profileResponse = await this.axios.post(Api.PATH_PROFILE, {
        access_token: accessToken,
        billing_profile_id: billingProfileId,
        contact_list: contactList,
      });
      return profileResponse;
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedBillingIndex = -1;
      });
      console.log('close');
    },
    closeRemove() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedBillingIndex = -1;
      });
      console.log('closeDelete');
    },
    isEditable: function () {
      switch (this.loginUser.roleId) {
        case UserRole.ADMIN:
        case UserRole.MANAGER:
          return true;
        default:
          return false;
      }
    },
    showAlert: function (type, message) {
      this.alert.isAlert = true;
      this.alert.type = type;
      this.alert.message = message;
    },
    onUpdateItemsPerPage: function(event, billing) {
      let itemsPerPage = parseInt(event, 10);
      const oldItemsPerPage = billing.itemsPerPage;
      if (oldItemsPerPage < 0 || oldItemsPerPage >= 10000000) {
        return;
      }
      billing.itemsPerPage = itemsPerPage; 
      const state = this.paginationState.find((state) => state.billingId === billing.billing_id);
      state.items.perPage = itemsPerPage;
      if (itemsPerPage < 0 || itemsPerPage >= 10000000) {
        setTimeout(() => billing.itemsPerPage = oldItemsPerPage);
      }
    },
    onPageChange: function (page, billing_id) {
      const state = this.paginationState.find((state) => state.billingId === billing_id);
      state.items.currentPage = page;
    },
    onSearch: function (word, billing_id) {
      const state = this.paginationState.find((state) => state.billingId === billing_id);
      state.items.searchWord = word;
    },
  },
};
</script>

<style>
.v-select__selections {
  line-height: 24px;
}
.v-list-item .v-list-item__title,
.v-list-item .v-list-item__subtitle {
  line-height: 1.5;
}
</style>

<style scoped>
.container {
  width: 95%;
}
.add-btn-height {
  height: 54px;
}
.restrict-email {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.td-icon-left {
  float: left;
}
.td-icon-right {
  float: right;
}
.td-button-inline {
  width: 110px !important;
  justify-content: flex-end;
}
.v-overlay {
  align-items: center;
  justify-content: center;
}
.v-data-table :deep(th),
.v-data-table :deep(.v-data-table-footer) {
  font-size: 12px !important;
}
.v-data-table :deep(td) {
  font-size: 14px !important;
}
.dialog-custom-row > .v-col {
  padding-bottom: 0;
}
</style>
